import type { AuthTokenResponse } from '@/features/auth/types'
import { AUTH_ENDPOINTS } from '@/features/auth/api/auth.endpoints'
import { bareHttp } from '@/core/api/http'
import {
  isHardRefreshFailure,
} from '@/core/auth/refresh-failure'
import {
  AUTH_REFRESH_LOCK_KEY,
  clearAuthTokens,
  getAccessToken,
  getAccessExpiresAt,
  isAccessTokenExpired,
  isLogoutPending,
  isRememberLogin,
  setAuthTokens,
  shouldRefreshAccessToken,
} from '@/core/auth/storage'

let refreshPromise: Promise<void> | null = null
let keeperTimer: ReturnType<typeof setInterval> | null = null

const KEEPER_INTERVAL_MS = 30_000
/** Peer must finish (or lock expire) before we steal the lock. */
const REFRESH_LOCK_TTL_MS = 20_000
/** Must be >= lock TTL so we don't silently succeed while lock still held. */
const REFRESH_WAIT_TIMEOUT_MS = 25_000
const REFRESH_WAIT_POLL_MS = 150

const GUEST_ROUTE_PREFIXES = [
  '/login',
  '/recruitment-portal/login',
  '/forgot-password',
  '/reset-password',
  '/setup-password',
]

export function isGuestAuthRoute(pathname = window.location.pathname): boolean {
  return GUEST_ROUTE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
}

export function redirectToLogin(): void {
  clearAuthTokens()
  stopAuthSessionKeeper()
  window.dispatchEvent(new CustomEvent('auth:session-cleared'))
  if (isGuestAuthRoute()) {
    return
  }
  // Guest QR stays public. Staff portal now shares the GEE login.
  const path = window.location.pathname
  if (path.startsWith('/r/')) {
    return
  }
  const redirect = `${path}${window.location.search}`
  const loginUrl = path.startsWith('/recruitment-portal') && redirect !== '/login'
    ? `/login?redirect=${encodeURIComponent(redirect)}`
    : '/login'
  window.location.replace(loginUrl)
}

function readRefreshLock(): number | null {
  const raw = localStorage.getItem(AUTH_REFRESH_LOCK_KEY)
  if (!raw) return null
  const lockedAt = Number(raw)
  return Number.isFinite(lockedAt) ? lockedAt : null
}

function acquireRefreshLock(): boolean {
  const now = Date.now()
  const lockedAt = readRefreshLock()
  if (lockedAt !== null && now - lockedAt < REFRESH_LOCK_TTL_MS) {
    return false
  }
  localStorage.setItem(AUTH_REFRESH_LOCK_KEY, String(now))
  return true
}

function releaseRefreshLock(): void {
  localStorage.removeItem(AUTH_REFRESH_LOCK_KEY)
}

export function isAuthRefreshLockHeld(): boolean {
  const lockedAt = readRefreshLock()
  return lockedAt !== null && Date.now() - lockedAt < REFRESH_LOCK_TTL_MS
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function waitForPeerRefresh(beforeExpiresAt: number | null): Promise<boolean> {
  const deadline = Date.now() + REFRESH_WAIT_TIMEOUT_MS
  while (Date.now() < deadline) {
    const lockedAt = readRefreshLock()
    if (lockedAt === null || Date.now() - lockedAt >= REFRESH_LOCK_TTL_MS) {
      break
    }

    const expiresAt = getAccessExpiresAt()
    if (expiresAt !== null && expiresAt !== beforeExpiresAt && expiresAt > Date.now()) {
      return true
    }

    await wait(REFRESH_WAIT_POLL_MS)
  }

  return !shouldRefreshAccessToken()
}

function syncAuthStoreToken(next: string | null): void {
  if (!next) return
  void import('@/features/auth/stores/auth.store')
    .then(({ useAuthStore }) => {
      useAuthStore().$patch({ token: next })
    })
    .catch(() => {
      // Pinia chưa sẵn sàng
    })
}

async function doRefreshSession(): Promise<void> {
  if (isLogoutPending()) {
    throw new Error('Refresh skipped during logout')
  }

  const { data } = await bareHttp.post<AuthTokenResponse>(
    AUTH_ENDPOINTS.REFRESH,
    {},
  )

  if (!data?.access_token) {
    throw new Error('Refresh response missing access_token')
  }

  setAuthTokens(
    {
      access_token: data.access_token,
      expires_in: data.expires_in,
    },
    isRememberLogin(),
  )

  syncAuthStoreToken(data.access_token)

  // Reverb private channels must re-auth / re-subscribe with the new PAT.
  window.dispatchEvent(new CustomEvent('auth:token-refreshed'))
}

export async function refreshSessionForced(): Promise<void> {
  if (isGuestAuthRoute()) {
    throw new Error('Refresh skipped on guest route')
  }

  if (!refreshPromise) {
    refreshPromise = (async () => {
      const beforeExpiresAt = getAccessExpiresAt()

      for (let attempt = 0; attempt < 3; attempt++) {
        if (!shouldRefreshAccessToken()) {
          return
        }

        if (!acquireRefreshLock()) {
          const peerRefreshed = await waitForPeerRefresh(beforeExpiresAt)
          if (peerRefreshed || !shouldRefreshAccessToken()) {
            syncAuthStoreToken(getAccessToken())
            window.dispatchEvent(new CustomEvent('auth:token-refreshed'))
            return
          }
          continue
        }

        try {
          await doRefreshSession()
          return
        } finally {
          releaseRefreshLock()
        }
      }

      if (shouldRefreshAccessToken()) {
        throw new Error('Refresh lock busy')
      }
    })().finally(() => {
      refreshPromise = null
    })
  }
  await refreshPromise
}

export async function ensureValidAccessToken(): Promise<void> {
  if (isGuestAuthRoute() || isLogoutPending() || !getAccessToken() || !shouldRefreshAccessToken()) {
    return
  }
  await refreshSessionForced()
}

function runAuthSessionKeeperTick(): void {
  if (isGuestAuthRoute() || !getAccessToken()) return
  void ensureValidAccessToken().catch((error) => {
    if (isHardRefreshFailure(error)) {
      redirectToLogin()
    }
    // Transient (network / 429 / lock) — keep session; next tick retries.
  })
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'visible') {
    runAuthSessionKeeperTick()
  }
}

export function startAuthSessionKeeper(): void {
  if (typeof window === 'undefined' || keeperTimer) return
  runAuthSessionKeeperTick()
  keeperTimer = setInterval(runAuthSessionKeeperTick, KEEPER_INTERVAL_MS)
  document.addEventListener('visibilitychange', onVisibilityChange)
  window.addEventListener('focus', runAuthSessionKeeperTick)
}

export function stopAuthSessionKeeper(): void {
  if (keeperTimer) {
    clearInterval(keeperTimer)
    keeperTimer = null
  }
  document.removeEventListener('visibilitychange', onVisibilityChange)
  window.removeEventListener('focus', runAuthSessionKeeperTick)
}

export { isHardRefreshFailure, isAccessTokenExpired }
